// React import
import React, { useState, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';

// Mui imports
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { grey } from '@mui/material/colors';

// FontAwesomeIcon imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faBuilding, faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons';

// Local import
import Header from '../../Header.js';
import AccountsMainFilters from '../accounts/AccountsMain.filters.js';

const AVATAR_COLORS = ['#0ea5e9', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#14b8a6'];

// Derive up to two initials from an email or display name.
function getInitials(value) {
    if (!value) return '?';
    const parts = String(value).replace(/@.*/, '').split(/[.\s_-]+/).filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

// Display name = the local part of an email, title-cased; otherwise the raw value.
function getDisplayName(value) {
    if (!value) return 'Unknown';
    const local = String(value).replace(/@.*/, '');
    return local.split(/[.\s_-]+/).filter(Boolean).map(w => w[0].toUpperCase() + w.slice(1)).join(' ') || value;
}

// Stable color per author so the same person always gets the same avatar.
function getAvatarColor(value) {
    let hash = 0;
    for (const ch of String(value || '')) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

// Human date-group label: Today / Yesterday / weekday (last 7d) / full date.
function getDayLabel(timestamp) {
    const d = dayjs(timestamp);
    if (d.isSame(dayjs(), 'day')) return 'Today';
    if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Yesterday';
    if (d.isAfter(dayjs().subtract(7, 'day'))) return d.format('dddd');
    return d.format('MMM D, YYYY');
}

function CompanyFeed(props) {
    // The outlet context exposes the organization under the `org` key.
    const { featureToggle, accounts, opportunities, seaPorts, org: organization } = useOutletContext();

    const navigate = useNavigate();

    return (
        <CompanyFeedInternal
            {...props}
            accounts={accounts}
            opportunities={opportunities}
            seaPorts={seaPorts}
            organization={organization}
            navigate={navigate}
            featureToggle={featureToggle}
        />
    );
}

function CompanyFeedInternal(props) {
    const { accounts, opportunities, seaPorts, organization, navigate, featureToggle } = props;

    const [ filter, setFilter ] = useState(() => {
        const stored = sessionStorage.getItem(`CompanyFeed.filter`);
        const base = stored ? JSON.parse(stored) : {};
        // AccountsMainFilters expects every search field to be an array. Coerce in case a
        // legacy session stored freetext as a string (or fields are missing entirely).
        return {
            tags: [], names: [], owners: [], transportationMethods: [],
            ...base,
            createdBy: Array.isArray(base.createdBy) ? base.createdBy : [],
            freetext: Array.isArray(base.freetext) ? base.freetext : (base.freetext ? [base.freetext] : []),
            advanced: {
                dateCreated: [], lastActivity: [], sources: [], destinations: [],
                potentialProfit: [ 0, 1000000 ], actualProfit: [ 0, 1000000 ],
                ...(base.advanced || {}),
            },
        };
    });

    // CargoWise sync updates are noisy, so hide them by default; the toggle below lets the
    // user bring them back. Persisted so the choice sticks across sessions.
    const [ showCargowise, setShowCargowise ] = useState(() => sessionStorage.getItem('CompanyFeed.showCargowise') === 'true');
    function toggleShowCargowise(value) {
        setShowCargowise(value);
        sessionStorage.setItem('CompanyFeed.showCargowise', String(value));
    }

    const allUpdates = useMemo(() => {
        return (accounts || [])
            .map(account => (account.updates || []).map(update => ({ account, update })))
            .flat()
            .sort((a,b) => b.update.created - a.update.created);
    }, [ accounts ]);

    const filteredUpdates = useMemo(() => {
        if (_.isEmpty(allUpdates) || !opportunities) return [];
        const filtered = (allUpdates || []).filter(({ update, account }) => {
            let toFilter = true;

            // Hide CargoWise sync updates unless the user opts in.
            if (!showCargowise && update.html?.includes('Account updated in Cargowise')) toFilter = false;

            filter.tags.forEach(tag => { if (!update.tags?.find(_tag => _.isEqual(tag, _tag))) toFilter = false; });
            filter.names.forEach(name => { if (account.name.indexOf(name) === -1) toFilter = false; });
            // owners = the account's owner; createdBy = the team member who wrote the update.
            filter.owners.forEach(name => { if ((account.owner || '').indexOf(name) === -1) toFilter = false; });
            filter.createdBy.forEach(name => { if ((update.createdBy || '').indexOf(name) === -1) toFilter = false; });
            if (filter.freetext.length > 0) {
                const haystack = JSON.stringify(update).toLowerCase();
                filter.freetext.forEach(term => { if (haystack.indexOf(String(term).toLowerCase()) === -1) toFilter = false; });
            }

            const lastActivity = _(account.updates).maxBy(o => o.created);
            if (filter.advanced.lastActivity.length === 2) {
                if (!lastActivity) {
                    toFilter = false;
                } else {
                    if (filter.advanced.lastActivity[0] > lastActivity.created) toFilter = false;
                    if (filter.advanced.lastActivity[1] < lastActivity.created) toFilter = false;
                }
            }

            if (!_.isEmpty(filter.transportationMethods)) {
                const op = opportunities.filter(o => o.account.id === account.id).map(o => o.transportationMethod);
                if (_.intersection(op, filter.transportationMethods).length === 0) toFilter = false;
            }

            return toFilter;
        });
        return filtered;

    }, [allUpdates, opportunities, filter, showCargowise]);

    const popularTabs = useMemo(() => {
        return _(filteredUpdates).map(({ update }) => update.tags)
            .compact()
            .flatten()
            .filter(tag => !filter.tags.find(_tag => _.isEqual(tag, _tag)))
            .countBy('label')
            .map((value, key) => ({ label: key, count: value }))
            .sortBy(a => -1 * a.count)
            .value();
    }, [filteredUpdates, filter.tabs]);

    const searchOptions = useMemo(() => {
        const tags = _(filteredUpdates)
            .map(a => a.update.tags)
            .flatten()
            .flatten()
            .filter(tag => !filter.tags.find(_tag => _.isEqual(tag, _tag)))
            .uniqBy(a => a?.label)
            .compact()
            .sortBy(a => a?.label)
            .value();
        const owners = _(accounts)
            .map(a => a.owner)
            .sort()
            .uniq()
            .compact()
            .value();
        return [
            ...tags.map(t => ({ type: 'tag', value: t })), 
            ...owners.map(n => ({ type: 'owner', value: n })),
        ]
    }, [accounts]);

    // Distinct account owners (for the Account owner filter).
    const ownerOptions = useMemo(
        () => _(accounts).map('owner').compact().uniq().sortBy(s => s.toLowerCase()).value(),
        [accounts]
    );

    // Distinct update authors across all updates (for the Created by filter).
    const createdByOptions = useMemo(
        () => _(allUpdates).map(u => u.update.createdBy).compact().uniq().sortBy(s => s.toLowerCase()).value(),
        [allUpdates]
    );

    // Group the chronological feed into day buckets, preserving newest-first order.
    const groupedUpdates = useMemo(() => {
        const groups = [];
        let current = null;
        filteredUpdates.forEach((item) => {
            const label = getDayLabel(item.update.created);
            if (!current || current.label !== label) {
                current = { label, items: [] };
                groups.push(current);
            }
            current.items.push(item);
        });
        return groups;
    }, [filteredUpdates]);

    function openAccount({ account, newWindow }) {
        const path = `/organization/${organization.alias}/accounts/${encodeURIComponent(account.id.toLowerCase())}/details`;
        if (newWindow) {
            // Same-origin so it works on localhost and any deployment, not just app.monada.ai.
            window.open(`${window.location.origin}${path}`, '_blank', 'noopener');
        } else {
            navigate(`../accounts/${encodeURIComponent(account.id.toLowerCase())}/details`);
        }
    }

    const loading = _.isEmpty(allUpdates) || !opportunities;

    return (
        <Box sx={{ paddingBottom: '48px', background: '#f8fafc', minHeight: '100%' }}>
            <Header
                icon={faList}
                title={'Company Feed'}
                buttons={[]}
            />

            {/* Centered, readable column for filters + feed */}
            <Box sx={{ maxWidth: '760px', margin: '0 auto', padding: '24px 16px 0' }}>
                <AccountsMainFilters
                    filter={filter}
                    setFilter={setFilter}
                    accounts={accounts}
                    seaPorts={seaPorts}
                    forceSearchOptions={searchOptions}
                    featureToggle={featureToggle}
                    moreFilters={popularTabs.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                            <Typography variant='caption' sx={{ color: grey[500] }}>
                                Popular tags:
                            </Typography>
                            {popularTabs.slice(0, 6).map(tab => (
                                <Chip
                                    key={tab.label}
                                    size='small'
                                    variant='outlined'
                                    clickable
                                    label={`${tab.label} · ${tab.count}`}
                                    onClick={() => setFilter({ ...filter, tags: [...filter.tags, { label: tab.label }] })}
                                    sx={{ height: '22px', fontSize: '11px', borderColor: grey[300] }}
                                />
                            ))}
                        </Box>
                    )}
                />

                {/* Dedicated people filters: account owner + update author */}
                <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', mt: '12px' }}>
                    <Autocomplete
                        multiple
                        size='small'
                        options={ownerOptions}
                        value={filter.owners}
                        onChange={(e, value) => setFilter(f => ({ ...f, owners: value }))}
                        getOptionLabel={o => getDisplayName(o)}
                        sx={{ flex: 1, minWidth: '220px' }}
                        renderInput={params => <TextField {...params} label='Account owner' placeholder={filter.owners.length ? '' : 'Any owner'} />}
                    />
                    <Autocomplete
                        multiple
                        size='small'
                        options={createdByOptions}
                        value={filter.createdBy}
                        onChange={(e, value) => setFilter(f => ({ ...f, createdBy: value }))}
                        getOptionLabel={o => getDisplayName(o)}
                        sx={{ flex: 1, minWidth: '220px' }}
                        renderInput={params => <TextField {...params} label='Created by' placeholder={filter.createdBy.length ? '' : 'Any team member'} />}
                    />
                </Box>

                {/* Result count / loading line */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '20px', mb: '4px', minHeight: '20px' }}>
                    {loading ? (
                        <>
                            <CircularProgress size={12} color='primary' />
                            <Typography variant='caption' sx={{ color: grey[500] }}>Loading updates…</Typography>
                        </>
                    ) : (
                        <Typography variant='caption' sx={{ color: grey[500], fontWeight: 600, letterSpacing: '0.02em' }}>
                            {filteredUpdates.length === 0 ? 'No updates match your filters'
                                : filteredUpdates.length === 1 ? '1 update'
                                : `${filteredUpdates.length} updates`}
                        </Typography>
                    )}
                    <Tooltip title='CargoWise sync updates are hidden by default. Turn this on to include them in the feed.' placement='bottom' arrow>
                        <FormControlLabel
                            sx={{ ml: 'auto', mr: 0, '& .MuiFormControlLabel-label': { fontSize: '12px', color: grey[600] } }}
                            control={<Switch size='small' checked={showCargowise} onChange={(e) => toggleShowCargowise(e.target.checked)} />}
                            label='Show CargoWise updates'
                            labelPlacement='start'
                        />
                    </Tooltip>
                </Box>

                {/* Empty state */}
                {!loading && filteredUpdates.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: '64px', color: grey[400] }}>
                        <FontAwesomeIcon icon={faList} style={{ fontSize: '28px', marginBottom: '12px' }} />
                        <Typography variant='body2' sx={{ color: grey[500] }}>
                            Nothing to show here yet.
                        </Typography>
                    </Box>
                )}

                {/* Date-grouped feed */}
                {groupedUpdates.map(group => (
                    <Box key={group.label} sx={{ mt: '20px' }}>
                        <Typography
                            sx={{
                                fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                                color: grey[500], mb: '10px', position: 'sticky', top: 0, zIndex: 1,
                                background: '#f8fafc', py: '4px',
                            }}
                        >
                            {group.label}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {group.items.map(({ account, update }) => (
                                <Paper
                                    key={`${account.id}-${update.created}`}
                                    elevation={0}
                                    sx={{
                                        display: 'flex', gap: '12px', p: '16px',
                                        border: `1px solid ${grey[200]}`, borderRadius: '12px', background: 'white',
                                        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
                                        '&:hover': { boxShadow: '0 2px 12px rgba(15,23,42,0.08)', borderColor: grey[300] },
                                    }}
                                >
                                    {/* Author avatar */}
                                    <Tooltip title={update.createdBy || ''} arrow>
                                        <Box sx={{
                                            flexShrink: 0, width: '38px', height: '38px', borderRadius: '50%',
                                            backgroundColor: getAvatarColor(update.createdBy), color: 'white',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '13px', fontWeight: 700,
                                        }}>
                                            {getInitials(update.createdBy)}
                                        </Box>
                                    </Tooltip>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        {/* Header row: author · time, account chip on the right */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '6px' }}>
                                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: grey[800] }}>
                                                {getDisplayName(update.createdBy)}
                                            </Typography>
                                            <Typography sx={{ fontSize: '12px', color: grey[400] }}>
                                                · {dayjs(update.created).fromNow()}
                                            </Typography>
                                            <Box sx={{ flex: 1 }} />
                                            <Chip
                                                size='small'
                                                clickable
                                                icon={<FontAwesomeIcon icon={faBuilding} style={{ fontSize: '10px' }} />}
                                                deleteIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '9px' }} />}
                                                onDelete={() => openAccount({ account, newWindow: true })}
                                                label={account.name}
                                                onClick={() => openAccount({ account, newWindow: true })}
                                                sx={{
                                                    maxWidth: '220px', height: '24px', fontSize: '12px', fontWeight: 500,
                                                    background: grey[100], color: grey[700], border: `1px solid ${grey[200]}`,
                                                    '& .MuiChip-icon': { color: grey[500], ml: '8px' },
                                                    '&:hover': { background: grey[200] },
                                                }}
                                            />
                                        </Box>

                                        {/* Update body */}
                                        <Box
                                            sx={{
                                                fontSize: '14px', lineHeight: 1.55, color: grey[800], wordBreak: 'break-word',
                                                '& p': { m: 0, mb: '4px' }, '& p:last-child': { mb: 0 },
                                                '& ul, & ol': { my: '4px', pl: '20px' }, '& a': { color: 'primary.main' },
                                                '& img': { maxWidth: '100%', borderRadius: '6px' },
                                            }}
                                            dangerouslySetInnerHTML={{ __html: update.html }}
                                        />

                                        {/* Tags */}
                                        {update.tags?.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mt: '10px' }}>
                                                {update.tags.map(tag => (
                                                    <Chip
                                                        key={tag.label}
                                                        size='small'
                                                        label={tag.label}
                                                        sx={{
                                                            height: '22px', fontSize: '11px', fontWeight: 500,
                                                            background: '#eef2ff', color: '#4338ca', border: 'none',
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

export default CompanyFeed;
